/**
 * /pack route (§18) — lightweight packer entry → the pack station. PACKER lands
 * here; MANAGER/ADMIN are allowed in for testing. Full PIN + 90-day station-trust
 * + remote revocation is the later AUTH increment — this is deliberately minimal
 * (pick a name / enter a PIN, no server validation yet). The session packer +
 * station are remembered per device.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSession } from "../components/auth.js";
import { isMockMode } from "../lib/mockMode.js";
import { PackStation } from "../packstation/PackStation.js";
import { BrandLockup, Button, Field, Input, Icon } from "../components/ui/index.js";

const PACKER_KEY = "cairn.packer";
const STATION_KEY = "cairn.stationId";

export function Pack() {
  const { user } = useSession();
  // Task 1 quick-pack deep link: /pack?order=<id> opens that order straight into
  // scanning (no batch, no dialog) — also how scan-to-pack from the queue lands.
  const [searchParams] = useSearchParams();
  const autoOrder = searchParams.get("order") || undefined;
  const [packer, setPacker] = useState<string | null>(() => {
    try {
      return localStorage.getItem(PACKER_KEY);
    } catch {
      return null;
    }
  });
  const [stationId] = useState<string>(() => {
    try {
      return localStorage.getItem(STATION_KEY) ?? (isMockMode() ? "demo-station" : "station-1");
    } catch {
      return "station-1";
    }
  });

  // A quick-pack deep link auto-starts a session so the packer lands in scanning
  // with a single click (the name comes from the signed-in operator).
  useEffect(() => {
    if (!autoOrder || packer) return;
    const name = user?.name ?? "Packer";
    try {
      localStorage.setItem(PACKER_KEY, name);
    } catch {
      /* session-only */
    }
    setPacker(name);
  }, [autoOrder, packer, user]);

  if (!packer) {
    return (
      <PackerEntry
        suggested={user?.name ?? ""}
        onStart={(name) => {
          try {
            localStorage.setItem(PACKER_KEY, name);
          } catch {
            /* session-only */
          }
          setPacker(name);
        }}
      />
    );
  }

  return (
    <PackStation
      stationId={stationId}
      autoOpenOrderId={autoOrder}
      onExit={() => {
        try {
          localStorage.removeItem(PACKER_KEY);
        } catch {
          /* noop */
        }
        setPacker(null);
      }}
    />
  );
}

function PackerEntry({ suggested, onStart }: { suggested: string; onStart: (name: string) => void }) {
  const [name, setName] = useState(suggested);
  const [pin, setPin] = useState("");
  const ready = name.trim().length > 0;
  return (
    <div className="packer-entry" data-density="comfortable">
      <form
        className="packer-entry__card"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) onStart(name.trim());
        }}
      >
        <BrandLockup subtitle="Pack station" />
        <h1 className="packer-entry__title">Start a pack session</h1>
        <Field label="Packer">
          <Input aria-label="Packer name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" autoFocus />
        </Field>
        <Field label="PIN (not required in this build)">
          <Input aria-label="PIN" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••" inputMode="numeric" type="password" />
        </Field>
        <Button type="submit" variant="primary" block icon="check" disabled={!ready}>
          Start packing
        </Button>
        <p className="packer-entry__hint">
          <Icon name="box" size={13} /> Full PIN + station trust arrives with the auth increment.
        </p>
      </form>
    </div>
  );
}
